// @flow

import _ from "lodash";
import assert from "assert";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import type { TaskType } from "./tasks";
import type { Divergence } from "../utils/";

import { boolToProb } from "../utils/";

export type Response = {
  kind: "response",
  confidence: number,
  lostFocus: boolean,
  secondsElapsed: number,
  searchesIds: Array<string>,
  secondsLimit: number
};

export type Timeout = {
  kind: "timeout",
  lostFocus: boolean,
  secondsLimit: number,
  secondsElapsed: number,
  searchesIds: Array<string>
};

export type Result = Response | Timeout;

// The class below organizes results for a single task by time limit

export class Results {
  task: TaskType;
  data: { [number]: Result }; // key is responseTimeLimit

  constructor(task: TaskType, data?: { [number]: Result }) {
    this.data = data || {};
    this.task = task;
  }

  add(result: Result): Results {
    return new Results(this.task, {
      [result.secondsLimit]: result,
      ...this.data
    });
  }

  finalResult(): ?Result {
    const maxTimeLimit = this.maxTimeLimit();
    return maxTimeLimit ? this.data[maxTimeLimit] : null;
  }

  finalSecondsElapsed(): ?number {
    const finalResult = this.finalResult();
    if (finalResult && finalResult.kind === "response") {
      return finalResult.secondsElapsed;
    } else {
      return null;
    }
  }

  finalError(divergence: Divergence, errorIfMissing: number): number {
    const finalResult = this.finalResult();
    if (finalResult && finalResult.kind === "response") {
      const truth = boolToProb(this.task.answer);
      const estimate = finalResult.confidence / 100;
      return divergence(truth, estimate);
    } else {
      return errorIfMissing;
    }
  }

  get(limit: number): ?Result {
    return this.data[limit];
  }

  toArray(): Array<Result> {
    return _.values(this.data);
  }

  latestTimeLimit(): ?number {
    return _.max(_.keys(this.data).map(_.toNumber));
  }

  maxTimeLimit(): ?number {
    return _.last(this.task.responseTimesInSeconds);
  }

  isComplete(): boolean {
    const latestTimeLimit = this.latestTimeLimit();
    const maxTimeLimit = this.maxTimeLimit();
    assert.ok(_.isInteger(maxTimeLimit));
    return latestTimeLimit === maxTimeLimit;
  }
}

const createResultMutation = gql`
  mutation(
    $kind: String!
    $confidence: Int
    $lostFocus: Boolean!
    $secondsElapsed: Float!
    $secondsLimit: Float!
    $searchesIds: [ID!]!
    $taskId: ID!
    $userId: ID!
    $sessionId: ID!
  ) {
    createResult(
      kind: $kind
      confidence: $confidence
      lostFocus: $lostFocus
      secondsElapsed: $secondsElapsed
      secondsLimit: $secondsLimit
      searchesIds: $searchesIds
      taskId: $taskId
      userId: $userId
      sessionId: $sessionId
    ) {
      id
    }
  }
`;

export const withCreateResult = graphql(createResultMutation, {
  name: "createResult"
});

export type createResultMutationResult = {
  data: {
    createResult: {
      id: string
    }
  }
};

export type createResultType = ({
  variables: {
    kind: "timeout" | "response",
    confidence: ?number,
    lostFocus: boolean,
    secondsElapsed: number,
    secondsLimit: number,
    taskId: string,
    userId: string
  }
}) => Promise<createResultMutationResult>;



// WEBPACK FOOTER //
// ./src/data/results.js